/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Editor/Stores/EndpointsEditorStore" />
import { ActionsHubManager } from "DistributedTask/Flux/ActionsHubManager";
import { StoreBase } from "DistributedTask/Flux/Store";
import { EndpointsEditorActionsHub, ErrorDetails } from "DistributedTaskUI/ServiceEndpoints/Editor/Actions/EndpointsEditorActionsHub";
import { EndpointSaveStatus } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/EndpointsEditorPanelInterfaces";
import * as Constants from "DistributedTaskUI/ServiceEndpoints/Editor/Constants";
import { InputDescriptor } from "ServiceEndpoints/Client/Contracts/FormInput_ServiceEndpoint";
import { OAuthConfiguration, ServiceEndpoint, ServiceEndpointType } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";

export interface IEndpointsEditorState {
    serviceEndpointType: ServiceEndpointType;
    serviceEndpoint: ServiceEndpoint;
    openToAllPipelinesFlag: boolean;
    isError: boolean;
    isDraft: boolean;
    errorMessage: string;
    activityId?: string;
    saveInProgress?: boolean;
    setupInProgress?: boolean;
    verifyInProgress?: boolean;
    configurations: OAuthConfiguration[];
}

export class EndpointsEditorStore extends StoreBase {
    constructor(context: IVssPageContext) {
        super("");
        this._state = EndpointsEditorStore.getDefaultState(context);
        this._actions = ActionsHubManager.GetActionsHub<EndpointsEditorActionsHub>(EndpointsEditorActionsHub);
        this._actions.setEndpointDraftMode.addListener(this._setEndpointDraftMode);
        this._actions.setEndpointCreationMode.addListener(this._setEndpointCreationMode);
        this._actions.setAuthenticationScheme.addListener(this._setServiceAuthenticationScheme);
        this._actions.setServiceEndpointType.addListener(this._setServiceEndpointType);
        this._actions.setServiceEndpoint.addListener(this._setServiceEndpoint);
        this._actions.setOAuthConfigurations.addListener(this._setOAuthConfigurations);
        this._actions.updateOpenToAllPipelinesFlag.addListener(this._updateOpenToAllPipelinesFlag);
        this._actions.initializeStateEndpoint.addListener(this._initializeStateEndpoint);
        this._actions.updateSaveProgress.addListener(this._updateSaveProgress);
        this._actions.setErrorMessage.addListener(this._setErrorMessage);
        this._actions.setErrorDetails.addListener(this._setErrorDetails);
        this._actions.removeEndpointCreationMode.addListener(this._removeEndpointCreationMode);
    }

    public disposeInternal(): void {
        this._actions.setEndpointDraftMode.removeListener(this._setEndpointDraftMode);
        this._actions.setEndpointCreationMode.removeListener(this._setEndpointCreationMode);
        this._actions.setAuthenticationScheme.removeListener(this._setServiceAuthenticationScheme);
        this._actions.setServiceEndpointType.removeListener(this._setServiceEndpointType);
        this._actions.setServiceEndpoint.removeListener(this._setServiceEndpoint);
        this._actions.setOAuthConfigurations.removeListener(this._setOAuthConfigurations);
        this._actions.updateOpenToAllPipelinesFlag.removeListener(this._updateOpenToAllPipelinesFlag);
        this._actions.initializeStateEndpoint.removeListener(this._initializeStateEndpoint);
        this._actions.updateSaveProgress.removeListener(this._updateSaveProgress);
        this._actions.setErrorMessage.removeListener(this._setErrorMessage);
        this._actions.setErrorDetails.removeListener(this._setErrorDetails);
        this._actions.removeEndpointCreationMode.removeListener(this._removeEndpointCreationMode);
    }

    public static getKey(): string {
        return "create-service-endpoints-store";
    }

    public getState(): IEndpointsEditorState {
        return this._state;
    }

    public static getDefaultState(context: IVssPageContext): IEndpointsEditorState {
        let serviceEndpoint: ServiceEndpoint = {
            administratorsGroup: null,
            authorization: {
                scheme: "",
                parameters: {}
            },
            createdBy: null,
            data: {},
            description: "",
            groupScopeId: null,
            id: undefined,
            isReady: undefined,
            isDisabled: undefined,
            name: "",
            operationStatus: null,
            readersGroup: null,
            serviceEndpointProjectReferences: null,
            type: "",
            url: "",
            isShared: false,
            isOutdated: false,
            owner: "library"
        };

        return {
            serviceEndpointType: null,
            serviceEndpoint: serviceEndpoint,
            openToAllPipelinesFlag: false,
            errorMessage: "",
            isError: false,
            isDraft: false,
            configurations: []
        };
    }

    private _setEndpointCreationMode = (creationMode: string) => {
        if (creationMode) {
            this._state.serviceEndpoint.data.creationMode = creationMode;
            this.emitChanged();
        }
    }

    private _removeEndpointCreationMode = (): void => {
        this._state.serviceEndpoint.data.creationMode = "";
        this.emitChanged();
    }

    private _setEndpointDraftMode = (isDraft: boolean) => {
        this._state.isDraft = isDraft;
        this.emitChanged();
    }

    private _setServiceAuthenticationScheme = (authScheme: string) => {
        let previousScheme = this._state.serviceEndpoint.authorization.scheme;
        this._state.serviceEndpoint.authorization.scheme = authScheme;
        this._resetEndpointAuthParameters(previousScheme);

        this.emitChanged();
    }

    private _setServiceEndpointType = (endpointType: ServiceEndpointType) => {
        let previousAuthScheme = this._state.serviceEndpoint.authorization.scheme;
        this._state.serviceEndpoint.authorization.scheme = endpointType.authenticationSchemes[0].scheme;
        let prevousEndpointType = this._state.serviceEndpointType;
        this._state.serviceEndpointType = endpointType;
        if (this._state.serviceEndpointType && prevousEndpointType !== endpointType) {
            this._state.serviceEndpoint.name = "";
            this._state.serviceEndpoint.description = "";

            this._state.serviceEndpointType.inputDescriptors.forEach((inputDescriptor: InputDescriptor) => {
                this._state.serviceEndpoint.data[inputDescriptor.id] =
                    inputDescriptor.values && inputDescriptor.values.defaultValue ? inputDescriptor.values.defaultValue : "";
            });

            this._resetEndpointAuthParameters(previousAuthScheme);

            this._state.serviceEndpoint.type = endpointType.name;
            this._state.serviceEndpoint.url = endpointType.endpointUrl && endpointType.endpointUrl.value ? endpointType.endpointUrl.value : "";
        }

        this.emitChanged();
    }

    private _setServiceEndpoint = (endpoint: ServiceEndpoint) => {
        let previousAuthScheme = this._state.serviceEndpoint.authorization.scheme;
        this._state.serviceEndpoint = endpoint;
        this._resetEndpointAuthParameters(previousAuthScheme);
        this._clearErrors();

        this.emitChanged();
    }

    private _setOAuthConfigurations = (configurations: OAuthConfiguration[]) => {
        this._state.configurations = configurations;
        this.emitChanged();
    }

    private _updateOpenToAllPipelinesFlag = (openToAllPipelinesFlag: boolean) => {
        this._state.openToAllPipelinesFlag = openToAllPipelinesFlag;

        this.emitChanged();
    }

    private _updateSaveProgress = (saveStatus: EndpointSaveStatus) => {
        if (saveStatus.verifyInProgress !== undefined) {
            this._state.verifyInProgress = saveStatus.verifyInProgress;
        }

        if (saveStatus.saveInProgress !== undefined) {
            this._state.saveInProgress = saveStatus.saveInProgress;
        }

        if (saveStatus.setupInProgress !== undefined) {
            this._state.setupInProgress = saveStatus.setupInProgress;
        }

        if (saveStatus.errorMessage !== undefined) {
            this._state.isError = !!saveStatus.errorMessage;
            this._state.errorMessage = saveStatus.errorMessage;
            this._state.activityId = saveStatus.activityId;
        }

        this.emitChanged();
    }

    private _setErrorMessage = (errorMessage: string) => {
        this._state.errorMessage = errorMessage;
        this._state.isError = true;
        this.emitChanged();
    }

    private _setErrorDetails = (details: ErrorDetails) => {
        this._state.errorMessage = details.errorMessage;
        this._state.isError = details.isError;
        this._state.activityId = details.activityId;
        this.emitChanged();
    }

    private _clearErrors(): void {
        this._state.isError = false;
        this._state.errorMessage = "";
    }

    private _resetEndpointAuthParameters(previousAuthScheme: string) {
        if (
            previousAuthScheme !== this._state.serviceEndpoint.authorization.scheme ||
            !this._state.serviceEndpoint.authorization.parameters ||
            Object.keys(this._state.serviceEndpoint.authorization.parameters).length === 0
        ) {
            let authSchemeIndex = this._state.serviceEndpointType.authenticationSchemes.findIndex((authScheme) => {
                return authScheme.scheme === this._state.serviceEndpoint.authorization.scheme;
            });

            if (authSchemeIndex !== -1) {
                this._state.serviceEndpoint.authorization.parameters = {};
                this._state.serviceEndpointType.authenticationSchemes[authSchemeIndex].inputDescriptors.forEach((inputDescriptor: InputDescriptor) => {
                    this._state.serviceEndpoint.authorization.parameters[inputDescriptor.id] = inputDescriptor.values && inputDescriptor.values.defaultValue
                        ? inputDescriptor.values.defaultValue : "";
                });
            }
        }
    }

    private _initializeStateEndpoint = (endpoint: ServiceEndpoint) => {
        this._state.serviceEndpoint = endpoint;
        if (Utils_String.equals(this._state.serviceEndpoint.authorization.scheme, Constants.OAuthScheme, true)) {
            this._state.serviceEndpoint.authorization.scheme = Constants.OAuth2Scheme;
        }

        if (Utils_String.equals(this._state.serviceEndpoint.authorization.scheme, Constants.PersonalAccessTokenScheme, true)) {
            this._state.serviceEndpoint.authorization.scheme = Constants.TokenScheme;

            if (this._state.serviceEndpoint.authorization.parameters.hasOwnProperty("accessToken")) {
                this._state.serviceEndpoint.authorization.parameters[Constants.AccessToken] =
                    this._state.serviceEndpoint.authorization.parameters["accessToken"];
                delete this._state.serviceEndpoint.authorization.parameters["accessToken"];
            }
        }

        this.emitChanged();
    }

    private _state: IEndpointsEditorState;
    private _actions: EndpointsEditorActionsHub;
}
